import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const getMonthGrid = (date) => {
  const y = date.getFullYear(), m = date.getMonth();
  const first = new Date(y, m, 1);
  const startDay = first.getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const eventTypeColor = {
  shoot: "#E53E3E", meeting: "#3B82F6", deadline: "#F59E0B", other: "#A1A1AA",
};

const CalendarPage = () => {
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", event_type: "meeting", start_at: "", notes: "", project_id: "" });

  const load = async () => {
    const [e, p] = await Promise.all([api.get("/calendar/events"), api.get("/projects")]);
    setEvents(e.data);
    setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const grid = getMonthGrid(cursor);

  const eventsOn = (d) => events.filter((ev) => {
    if (!ev.start_at) return false;
    const dt = new Date(ev.start_at);
    return dt.getFullYear() === d.getFullYear() && dt.getMonth() === d.getMonth() && dt.getDate() === d.getDate();
  });

  // Also show shoot dates from projects that don't have events
  const projectShoots = projects.filter(p => p.shoot_date).map(p => ({
    id: `p-${p.id}`,
    title: `Shoot: ${p.project_title}`,
    event_type: "shoot",
    start_at: p.shoot_date,
    project_id: p.id,
  }));
  const allEvents = [...events, ...projectShoots.filter(ps => !events.find(e => e.project_id === ps.project_id && e.event_type === "shoot" && e.start_at?.startsWith(ps.start_at)))];

  const eventsOnAll = (d) => allEvents.filter((ev) => {
    if (!ev.start_at) return false;
    const dt = new Date(ev.start_at);
    return dt.getFullYear() === d.getFullYear() && dt.getMonth() === d.getMonth() && dt.getDate() === d.getDate();
  });

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.project_id) delete payload.project_id;
      await api.post("/calendar/events", payload);
      toast.success("Event created");
      setShowNew(false);
      setForm({ title: "", event_type: "meeting", start_at: "", notes: "", project_id: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="label-caps mb-2">Scheduling</div>
          <h1 className="font-editorial text-6xl tracking-tighter leading-none">Calendar</h1>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-event-btn">
          <Plus size={14} /> New Event
        </button>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="btn-ghost !p-3" data-testid="cal-prev"><ChevronLeft size={14} /></button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="btn-ghost !p-3" data-testid="cal-next"><ChevronRight size={14} /></button>
          <button onClick={() => setCursor(new Date())} className="label-caps hover:text-white transition" data-testid="cal-today">Today</button>
        </div>
        <div className="font-editorial text-3xl tracking-tight" data-testid="cal-month">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
      </div>

      <div className="grid grid-cols-7 gap-px bg-white/5 border border-white/5">
        {DAYS.map((d) => (
          <div key={d} className="bg-[#0A0A0A] p-3 label-caps">{d}</div>
        ))}
        {grid.map((d, i) => (
          <div key={i} className={`bg-[#050505] min-h-[120px] p-3 ${d ? "" : "opacity-30"}`} data-testid={d ? `day-${d.getDate()}` : undefined}>
            {d && (
              <>
                <div className="font-mono-alt text-xs text-white/40 mb-2">{d.getDate()}</div>
                <div className="space-y-1">
                  {eventsOnAll(d).slice(0, 3).map((ev) => (
                    <div key={ev.id} className="text-xs px-2 py-1 bg-white/5 border-l-2 truncate"
                      style={{ borderLeftColor: eventTypeColor[ev.event_type] || "#A1A1AA" }}>
                      {ev.title}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-8" onClick={() => setShowNew(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={createEvent}
            className="bg-[#0A0A0A] border border-white/10 p-10 w-full max-w-xl">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-editorial text-3xl">New Event</h2>
              <button type="button" onClick={() => setShowNew(false)}><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-caps mb-2 block">Title</label>
                <input required className="input-editorial" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="ev-title"/>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps mb-2 block">Type</label>
                  <select className="input-editorial" value={form.event_type} onChange={(e) => setForm({...form, event_type: e.target.value})} data-testid="ev-type">
                    <option value="shoot">Shoot</option>
                    <option value="meeting">Meeting</option>
                    <option value="deadline">Deadline</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="label-caps mb-2 block">Date</label>
                  <input required type="date" className="input-editorial" value={form.start_at} onChange={(e) => setForm({...form, start_at: e.target.value})} data-testid="ev-date"/>
                </div>
              </div>
              <div>
                <label className="label-caps mb-2 block">Project (optional)</label>
                <select className="input-editorial" value={form.project_id} onChange={(e) => setForm({...form, project_id: e.target.value})}>
                  <option value="">— None —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.project_title}</option>)}
                </select>
              </div>
              <div>
                <label className="label-caps mb-2 block">Notes</label>
                <textarea rows={3} className="input-editorial" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
              </div>
            </div>
            <button type="submit" className="btn-primary w-full mt-8" data-testid="ev-submit">Create Event</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
